import { type FC, useEffect, useMemo, useState } from "react";
import {
  lineChartFactory,
  noBarData,
  noData,
  validLineData,
  chartColorScale,
  type QueryFilter,
  lineChartTitles,
  chartLegendLabels,
  totalActivity,
  dailyActivity,
  starts,
  completes,
  fallbackToZero,
  createDateRange,
} from "features/Library/Common/utils/performanceUtils";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { reportingColors } from "features/Library/Common/utils/constants";
import { ChartWrapper, HorizontalBarChart, LineChart, getDateFormatByCount } from "components/charts";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type ColumnOption } from "interfaces/columnOptions";
import { GenericPerformanceList } from "../../shared/GenericPerformanceList";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { type VideoDetails } from "features/Reporting/types/content";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { useQuery } from "@tanstack/react-query";
import * as contentReportingService from "features/Reporting/services/contentReportingService";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import {
  type EngagementLineData,
  type VideoEngagementData,
} from "features/Library/Videos/state/slices/videoPerformanceSlice";
import { sharedAccountReportingHorizontalBarProps, sharedAccountReportingLineProps } from "../../shared";
import { type CommonAccountReportPropsExtended } from "../../types";
import dateTimeUtils from "utils/dateTimeUtils";
import { EntityType } from "features/Library/Flows/Designer/types";

import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { doVideoExport } from "features/Reporting/state/content/contentActions";
import { bindActionCreators } from "@reduxjs/toolkit";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import styles from "../../contentOverviewReports.module.scss";
import { AddOnIds, AssetTypes } from "enums";
import { useAddOnQuery } from "../../queries/useAddOnQuery";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";

export type ExtractsEvents<T, K extends keyof T> = T[K];
export type Events = ExtractsEvents<EngagementLineData[number], "Events">;
export type RestructuredDay = Events & Pick<EngagementLineData[number], "Date">;

const getCardsReportData = (engagementData: VideoEngagementData): CardReportingItem[] => {
  return [
    {
      statDescription: "Accounts",
      popupBody: "The total number of customers across all videos.",
      stat: fallbackToZero(engagementData.Customers),
    },
    {
      statDescription: starts,
      popupBody: "The sum of how often this video has been started.",
      stat: fallbackToZero(engagementData.StartedCount),
    },
    {
      statDescription: completes,
      popupBody: "The sum of how often this video has been completed.",
      stat: fallbackToZero(engagementData.CompletedCount),
    },
    {
      statDescription: "Completion Rate",
      popupBody: "The percentage of videos completed compared to the sum of how often this video has been started.",
      stat: fallbackToZero(engagementData.CompletionRate) * 100,
      isStatPercentage: true,
    },
  ];
};

export type Props = CommonAccountReportPropsExtended & PropsFromRedux;

const fetchDailyData = async ({ queryKey }: QueryFilter) => {
  let result;
  result = await contentReportingService.getVideoDailyData({ ...queryKey[1], allItems: true });
  return lineChartFactory(result.data);
};

const fetchEngagementQuery = async ({ queryKey }: QueryFilter) => {
  let result;
  result = (await contentReportingService.getVideoEngagement({ ...queryKey[1], allItems: true })).data;

  const usageBarData: DataPoint[] = [
    {
      id: starts.toLowerCase(),
      category: starts,
      value: result.StartedCount,
      fillColor: reportingColors.blue,
    },
    {
      id: completes.toLowerCase(),
      category: completes,
      value: result.CompletedCount,
      fillColor: reportingColors.yellow,
    },
  ];
  return { ...result, usageBarData };
};

const fetchDetailsQuery = async ({ queryKey }: QueryFilter) => {
  let result;
  result = (await contentReportingService.getVideoDetails({ ...queryKey[1], allItems: true })).data;
  return result.map((item, index) => {
    const roundedCompletionRate = roundToTwoDigits(item.CompletionRate as number);
    return {
      ...item,
      CompletionRate: roundedCompletionRate,
      id: index,
    };
  });
};

export const VideoReport: FC<Props> = ({
  filter,
  setSelectedContent,
  setHasExportData,
  setExportAction,
  videoExport,
  setIsReportEnabled,
  resetIsReportEnabled,
  accountId,
  disableLinks,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = useFeatureFlag(FeatureFlags.VideoPerformanceOverview);

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["VideoReportDailyData", { ...filter, aggregation: chartPeriod }],
    queryFn: fetchDailyData,
    enabled: reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["VideoReportEngagement", filter],
    queryFn: fetchEngagementQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  const detailsQuery = useQuery({
    queryKey: ["VideoReportDetails", filter],
    queryFn: fetchDetailsQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: !!detailsQuery.data && detailsQuery.data.length > 0,
      tooltipContent: "No data available for export. There are no videos with activity data.",
    });
  }, [detailsQuery.data, setHasExportData]);

  // Setting up correct export method if send entity is changed
  useEffect(() => {
    setExportAction({
      method: videoExport,
      args: [{ ...filter, allItems: true }],
      isExportEnabled: true,
    });
  }, [videoExport, filter, setExportAction, accountId]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const lineData = useMemo(() => {
    if (chartState === dailyActivity) {
      return {
        xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
        yData: [lineChartQuery.data?.StartedCount, lineChartQuery.data?.CompletedCount],
      };
    }
    return {
      xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
      yData: [lineChartQuery.data?.CumulativeStartedCount, lineChartQuery.data?.CumulativeCompletedCount],
    };
  }, [chartState, lineChartQuery.data]);

  const { data: addons } = useAddOnQuery(accountId);
  const hasCreateAccount = addons?.find((addon) => addon.id === AddOnIds.CreateAccounts)?.isEnabled;

  const cardData = useMemo(() => {
    if (engagementQuery.isSuccess) {
      const cards = getCardsReportData(engagementQuery.data);
      if (!hasCreateAccount) {
        return cards.slice(1);
      }
      return cards;
    }
  }, [engagementQuery.data, engagementQuery.isSuccess, hasCreateAccount]);

  const columns: ColumnOption<VideoDetails>[] = useMemo(
    () => [
      {
        name: "Title",
        width: 7,
        isSortable: false,
        render(item) {
          const titleContent = (
            <TextTruncate lines={2} className={styles.wordBreak}>
              {item.VideoTitle}
            </TextTruncate>
          );
          return (
            <Tooltip
              target={
                disableLinks ? (
                  titleContent
                ) : (
                  <button
                    className={styles.linkButton}
                    onClick={() => {
                      if (item.VideoType === AssetTypes.Video) {
                        return setSelectedContent({ id: item.VideoId, type: EntityType.Video, name: item.VideoTitle });
                      } else {
                        return setSelectedContent({
                          id: item.VideoId,
                          type: EntityType.EmbeddedVideo,
                          name: item.VideoTitle,
                        });
                      }
                    }}
                  >
                    {titleContent}
                  </button>
                )
              }
              content={item.VideoTitle}
              showAlways
            />
          );
        },
      },
      {
        name: "Type",
        width: 4,
        isSortable: false,
        render(item) {
          if (item.VideoType === AssetTypes.Video) {
            return <div>Uploaded</div>;
          } else if (item.VideoType === AssetTypes.EmbeddedVideo) {
            return <div>Embedded</div>;
          }
        },
      },
      {
        isSortable: false,
        name: starts,
        render(item) {
          return <div>{item.TotalStarts.toLocaleString()}</div>;
        },
        width: 4,
      },
      {
        isSortable: false,
        width: 4,
        name: completes,
        render(item) {
          return <div>{item.TotalCompletes.toLocaleString()}</div>;
        },
      },
      {
        width: 4,
        render(item) {
          return <div>{roundToTwoDigits((item.CompletionRate as number) * 100)}%</div>;
        },
        name: "Completion Rate",
        isSortable: false,
      },
      {
        name: "Last Activity",
        width: 4,
        isSortable: false,
        render(item) {
          return <div>{dateTimeUtils.formatDate(item.LastActivity)}</div>;
        },
      },
    ],
    [setSelectedContent, disableLinks],
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={lineChartTitles}
          showLegend
          legendLabels={chartLegendLabels}
          colorRange={chartColorScale}
          onChange={setChartState}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([
              lineChartQuery.data.CompletedCount,
              lineChartQuery.data.CumulativeCompletedCount,
              lineChartQuery.data.StartedCount,
              lineChartQuery.data.CumulativeStartedCount,
            ]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
                colorRange={chartColorScale}
                {...lineData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={["Engagement"]} showLegend legendLabels={chartLegendLabels} colorRange={chartColorScale}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess &&
            !noBarData(engagementQuery.data.StartedCount, engagementQuery.data.CompletedCount) ? (
              <HorizontalBarChart
                {...sharedAccountReportingHorizontalBarProps}
                data={engagementQuery.data.usageBarData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.performanceCardSection}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={cardData!} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <RequestStatusRenderer state={detailsQuery.status}>
          {detailsQuery.isSuccess && (
            <GenericPerformanceList title="Videos" rows={detailsQuery.data} columns={columns} filter={filter} />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  videoExport: bindActionCreators(doVideoExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(VideoReport);
export default ConnectedComponent;
